/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Stack, CircularProgress, Box, Skeleton, Button } from '@mui/material';
import { AI_SUMMARY_TYPES } from '@/features/factFind/factFindApi';
import { useDispatch } from 'react-redux';
import Robot from '@/assets/images/ai-assistant.gif';
import { useParams } from 'react-router-dom';
import useGetAIMessages from '@/hooks/useGetAIMessages';
import { aiWorkflowApi, useCreateUserAIMessageMutation } from '@/features/aiWorkflow/aiWorkflowApi';
import { showToast } from '@/features/toast/toastSlice';
import { AI_WORKFLOW_STATUSES, MAX_AI_MESSAGE_LENGTH } from '@/features/aiWorkflow/constants';
import useGetAIWorkflows from '@/hooks/useGetAIWorkflows';
import MarkdownEditor from '@/components/MarkdownEditor';
import UnsavedChangesActions from '@/components/UnsavedChangesActions';

const GeneratingInProgress = ({ title }) => {
	return (
		<>
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '41%',
					borderRadius: '50%',
					zIndex: 5,
					height: '150px',
					width: '150px',
					backgroundImage: `url(${Robot})`,
					backgroundPosition: 'left',
					backgroundSize: 'cover',
					backgroundRepeat: 'no-repeat',
					display: 'flex',
					flexDirection: 'column',
				}}
			/>
			<Stack direction='row' spacing={1} alignItems='center' pb='1em'>
				<CircularProgress size={'1.25em'} />
				<Typography>{`Generating ${title.toLowerCase()} data. Please check back soon.`}</Typography>
			</Stack>
		</>
	);
};

export const FactFindForm = ({ title, factFindType }) => {
	const { dealId } = useParams();
	const dispatch = useDispatch();

	const isRiskInfo = factFindType === 'risk';
	const isQuote = factFindType === 'quote';
	const workflowType = isQuote ? AI_SUMMARY_TYPES.quoteSummary : AI_SUMMARY_TYPES.riskInfoSummary;

	const { getLatestWorkflow, ...workflowQuery } = useGetAIWorkflows({ dealId });
	const latestWorkflow = getLatestWorkflow(workflowType);

	useEffect(() => console.log('Current work flows', latestWorkflow), [latestWorkflow]);

	const isGenerating =
		latestWorkflow &&
		![AI_WORKFLOW_STATUSES.ERROR, AI_WORKFLOW_STATUSES.SUCCESS].includes(
			latestWorkflow?.status,
		);

	const { getMessages, ...aiMessagesState } = useGetAIMessages({
		dealId,
	});

	const summaries = useMemo(() => {
		const data = getMessages({
			summaryType: isQuote ? AI_SUMMARY_TYPES.quoteSummary : AI_SUMMARY_TYPES.riskInfoSummary,
		});
		return data.sort((a, b) => b.dateCreated?.seconds - a.dateCreated?.seconds);
	}, [
		isQuote,
		aiMessagesState.isLoading,
		aiMessagesState.isUninitialized,
		aiMessagesState.isError,
		aiMessagesState.isSuccess,
	]);

	// null until the user edits; Cancel resets it to show the latest saved summary again
	const [draft, setDraft] = useState(null);
	const [createUserAIMessage, { isLoading: isSaving }] = useCreateUserAIMessageMutation();

	const savedMessage = summaries?.[0]?.message ?? '';
	const isUnsaved = draft !== null && draft !== savedMessage;

	const handleSave = async () => {
		// The Firestore rules reject longer messages, which would otherwise show the generic error
		if (draft.length > MAX_AI_MESSAGE_LENGTH) {
			dispatch(
				showToast({
					message: `Summary is too long to save. Please shorten it to under ${MAX_AI_MESSAGE_LENGTH.toLocaleString()} characters.`,
					error: true,
				}),
			);
			return;
		}

		const result = await createUserAIMessage({
			dealId,
			summaryType: workflowType,
			message: draft,
			editedFrom: summaries[0]?.id,
		});
		if (result.error) {
			dispatch(
				showToast({ message: 'Could not save summary. Please try again.', error: true }),
			);
			return;
		}
		// The new doc arrives through useGetAIMessages and becomes the latest summary
		setDraft(null);
		dispatch(showToast({ message: 'Summary saved successfully!', success: true }));
	};

	const isError =
		latestWorkflow?.status === AI_WORKFLOW_STATUSES.ERROR || aiMessagesState.isError;

	useEffect(() => {
		let intervalId;

		const checkRunStatus = async () => {
			dispatch(
				aiWorkflowApi.util.invalidateTags([
					{ type: 'AI_WORKFLOW', id: `${dealId}-${workflowType}` },
				]),
			);
		};

		if (isGenerating) {
			intervalId = setInterval(checkRunStatus, 20000); // Check every 20 seconds
		}

		return () => clearInterval(intervalId);
	}, [isGenerating, dealId, workflowType]);

	const loadingInProgress =
		workflowQuery.isLoading ||
		workflowQuery.isUninitialized ||
		aiMessagesState.isLoading ||
		aiMessagesState.isUninitialized;

	if (loadingInProgress) {
		return (
			<Box pr='0.5em'>
				<Typography variant='subtitle2' sx={{ fontWeight: 'bold', fontSize: '20px' }}>
					{`${title} details`}
				</Typography>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						width: '100%',
						alignItems: 'center',
						pt: '0.5em',
					}}
				>
					<Skeleton animation='wave' height='15px' width='100%' />
				</Box>
			</Box>
		);
	}

	if (!isGenerating && summaries.length === 0) {
		return (
			<Typography variant='subtitle2' mt='1.5em'>
				{`Looks empty! Upload a ${isQuote ? 'Quote Comparison PDF' : 'Policy Schedule or Fact Find PDF'} to generate information.`}
			</Typography>
		);
	}

	if (isError) {
		return (
			<Typography variant='subtitle2' mt='1.5em'>
				There was an error extracting info from uploaded document. Please try again.
			</Typography>
		);
	}

	return (
		<Box pr='0.5em'>
			<Typography variant='subtitle2' sx={{ fontWeight: 'bold', fontSize: '20px' }}>
				{`${title} details`}
			</Typography>

			<Stack spacing={1}>
				{isGenerating ? (
					<GeneratingInProgress title={title} />
				) : (
					<>
						<Typography variant='subtitle2' fontSize={'15px'}>
							{'Summary'}
						</Typography>

						<MarkdownEditor
							value={draft ?? savedMessage}
							setValue={setDraft}
							height='80vh'
						/>
						<UnsavedChangesActions
							isUnsaved={isUnsaved}
							sx={{ mt: 0, borderTop: 'none' }}
						>
							<Button
								variant='outlined'
								disabled={!isUnsaved || isSaving}
								onClick={() => setDraft(null)}
							>
								Cancel
							</Button>
							<Button
								variant='contained'
								disabled={!isUnsaved || isSaving}
								onClick={handleSave}
							>
								{isSaving ? <CircularProgress size={18} color='inherit' /> : 'Save'}
							</Button>
						</UnsavedChangesActions>
					</>
				)}
			</Stack>
		</Box>
	);
};
